import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api-client';
import { useAuth } from './use-auth';
import { PERMISSION_RESOURCE_ALIASES } from '../config/permissions';

const ALLOWED_ACTIONS = new Set(['view', 'create', 'update', 'delete', 'manage']);

function hasPermission(accessContext, resource, action) {
  if (!accessContext) {
    return false;
  }

  if (accessContext.role === 'admin' || accessContext.role === 'owner') {
    return true;
  }

  const normalizedAction = String(action || '').toLowerCase().trim();
  if (!ALLOWED_ACTIONS.has(normalizedAction)) {
    return false;
  }

  const candidates = [resource, ...(PERMISSION_RESOURCE_ALIASES?.[resource] || [])];
  return candidates.some((candidate) => {
    const resourcePermissions = accessContext.permissions?.[candidate] || [];
    return resourcePermissions.includes('manage') || resourcePermissions.includes(normalizedAction);
  });
}

function meetsPermissionRequirement(accessContext, requirements = {}) {
  if (!requirements || typeof requirements !== 'object') {
    return false;
  }

  for (const [resource, requiredActions] of Object.entries(requirements)) {
    if (!Array.isArray(requiredActions) || requiredActions.length === 0) {
      return false;
    }

    const authorized = requiredActions.every((action) => hasPermission(accessContext, resource, action));
    if (!authorized) {
      return false;
    }
  }

  return true;
}

export function usePermission(requirements) {
  const { isAuthenticated } = useAuth();
  const { data, isPending } = useQuery({
    queryKey: ['organization-access-me'],
    queryFn: () => apiGet('organization/access/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(isAuthenticated),
  });

  const access = data?.status ? data.data : null;
  const accessContext = useMemo(
    () => ({
      role: access?.membership?.role,
      permissions: access?.membership?.permissions || {},
    }),
    [access?.membership?.permissions, access?.membership?.role]
  );

  const allowed = useMemo(() => {
    if (!requirements) {
      return true;
    }
    return meetsPermissionRequirement(accessContext, requirements);
  }, [accessContext, requirements]);

  return {
    allowed,
    isLoading: Boolean(isAuthenticated) && isPending,
    access,
    accessContext,
    hasPermission: (resource, action) => hasPermission(accessContext, resource, action),
    hasPermissions: (reqs) => meetsPermissionRequirement(accessContext, reqs),
  };
}
